import { useState } from "react";
import { useModals } from "../components/common/ModalsProvider.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

type Role = "technician" | "manager" | "client" | "viewer" | "user" | "admin";
type Lang = "fr" | "en" | "pt";

export default function Invites() {
  const { user } = useAuth();
  const modals = useModals();
  const t = useT();
  const adminCompanyIds = user
    ? user.isSuperAdmin
      ? user.allowedCompanyIds
      : user.memberships.filter((m) => m.active && m.role === "admin").map((m) => m.companyId)
    : [];

  const [companyId, setCompanyId] = useState<number | null>(adminCompanyIds[0] ?? null);
  const companiesQuery = trpc.companies.list.useQuery();
  const invitesQuery = trpc.invites.listByCompany.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );

  const utils = trpc.useUtils();
  const revokeMutation = trpc.invites.revoke.useMutation({
    onSuccess: () => invitesQuery.refetch(),
  });
  const resendMutation = trpc.invites.resend.useMutation({
    onSuccess: () => invitesQuery.refetch(),
  });

  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formCompanyIds, setFormCompanyIds] = useState<number[]>(adminCompanyIds);
  const [formRole, setFormRole] = useState<Role>("viewer");
  const [formLang, setFormLang] = useState<Lang>("fr");
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = trpc.invites.create.useMutation({
    onSuccess: (res) => {
      setShowForm(false);
      setFormName("");
      setFormEmail("");
      setFormCompanyIds(adminCompanyIds);
      setFormRole("viewer");
      setFormLang("fr");
      setFormError(null);
      invitesQuery.refetch();
      utils.users.listByCompany.invalidate();
      const skipped = res?.skipped ?? [];
      void modals.alert({
        title: t("invites.inviteSentTitle"),
        message:
          `${t("invites.accessGrantedTo")} ${(res?.granted ?? []).join(", ") || "—"}.` +
          (skipped.length ? ` ${t("invites.alreadyHadAccess")} ${skipped.join(", ")}.` : ""),
      });
    },
    onError: (err) => setFormError(err.message),
  });

  function toggleFormCompany(id: number) {
    setFormCompanyIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  if (!user) return null;

  const adminCompanies = (companiesQuery.data ?? []).filter((c) => adminCompanyIds.includes(c.id));

  if (adminCompanies.length === 0) {
    return (
      <Shell title={t("invites.pageTitle")}>
        <p className="text-sm text-neutral-400">{t("invites.notAdminOfAnyCompany")}</p>
      </Shell>
    );
  }

  return (
    <Shell title={t("invites.pageTitle")}>
      <div className="flex items-center gap-3">
        <label className="text-sm text-neutral-400">{t("invites.companyLabel")}</label>
        <select
          value={companyId ?? ""}
          onChange={(e) => setCompanyId(Number(e.target.value))}
          className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none"
        >
          {adminCompanies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <div className="flex-1" />
        <button
          onClick={() => setShowForm((v) => !v)}
          className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium transition-colors"
        >
          {showForm ? t("invites.cancel") : t("invites.newInviteBtn")}
        </button>
      </div>

      {showForm && (
        <form
          className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError(null);
            if (formCompanyIds.length === 0) {
              setFormError(t("invites.selectAtLeastOneCompany"));
              return;
            }
            createMutation.mutate({
              companyIds: formCompanyIds,
              email: formEmail,
              name: formName,
              role: formRole,
              lang: formLang,
            });
          }}
        >
          {/* F-per-company — empresas (multi-seleção): o usuário terá acesso a TODAS as marcadas */}
          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              {t("invites.companiesLabel")}
            </label>
            <div className="flex flex-wrap gap-2">
              {adminCompanies.map((c) => {
                const on = formCompanyIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleFormCompany(c.id)}
                    disabled={createMutation.isPending}
                    className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      on
                        ? "border-lime bg-lime/10 text-lime"
                        : "border-neutral-800 text-neutral-300 hover:border-neutral-700"
                    }`}
                  >
                    {on ? "✓ " : ""}
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-neutral-400 mb-1">{t("invites.name")}</label>
              <input
                required
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                disabled={createMutation.isPending}
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm focus:border-lime outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-neutral-400 mb-1">{t("invites.email")}</label>
              <input
                type="email"
                required
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                disabled={createMutation.isPending}
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm focus:border-lime outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-neutral-400 mb-1">
                {t("invites.permission")}
              </label>
              <select
                value={formRole}
                onChange={(e) => setFormRole(e.target.value as Role)}
                disabled={createMutation.isPending}
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm focus:border-lime outline-none"
              >
                {/* F-tech-portal — convite por e-mail para conta do portal. */}
                <option value="technician">{t("invites.roleTechnicianOption")}</option>
                <option value="manager">{t("invites.roleManagerOption")}</option>
                <option value="client">{t("invites.roleClientOption")}</option>
                <option value="viewer">{t("invites.roleViewerOption")}</option>
                <option value="user">{t("invites.roleUserOption")}</option>
                <option value="admin">{t("invites.roleAdminOption")}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-neutral-400 mb-1">
                {t("invites.emailLang")}
              </label>
              <select
                value={formLang}
                onChange={(e) => setFormLang(e.target.value as Lang)}
                disabled={createMutation.isPending}
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-sm focus:border-lime outline-none"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>
          </div>
          {formError && (
            <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {formError}
            </div>
          )}
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {createMutation.isPending ? t("invites.sending") : t("invites.sendInvite")}
          </button>
        </form>
      )}

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {invitesQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("invites.loading")}</p>
        )}
        {invitesQuery.error && (
          <p className="p-4 text-sm text-red-400">{invitesQuery.error.message}</p>
        )}
        {invitesQuery.data && invitesQuery.data.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("invites.noInvites")}</p>
        )}
        {invitesQuery.data && invitesQuery.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">{t("invites.name")}</th>
                <th className="text-left px-3 py-2">{t("invites.email")}</th>
                <th className="text-left px-3 py-2">{t("invites.permission")}</th>
                <th className="text-left px-3 py-2">{t("invites.status")}</th>
                <th className="text-left px-3 py-2">{t("invites.expires")}</th>
                <th className="text-left px-3 py-2">{t("invites.invitedBy")}</th>
                <th className="px-3 py-2 text-right">{t("invites.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {invitesQuery.data.map((inv) => (
                <tr key={inv.id}>
                  <td className="px-3 py-2">{inv.name}</td>
                  <td className="px-3 py-2 text-neutral-400">{inv.email}</td>
                  <td className="px-3 py-2">
                    <RoleBadge role={inv.role as Role} />
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={inv.status} />
                  </td>
                  <td className="px-3 py-2 text-neutral-500 text-xs">
                    {new Date(inv.expiresAt).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-3 py-2 text-neutral-500 text-xs">{inv.invitedByName ?? "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {inv.status === "pending" && companyId !== null && (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => resendMutation.mutate({ companyId, inviteId: inv.id })}
                          disabled={resendMutation.isPending}
                          className="rounded-md border border-neutral-800 hover:border-neutral-700 px-2 py-1 text-xs"
                        >
                          {t("invites.resend")}
                        </button>
                        <button
                          onClick={async () => {
                            const ok = await modals.confirm({
                              title: t("invites.revokeInviteTitle"),
                              message: `${t("invites.revokeConfirmPre")} ${inv.email}${t("invites.revokeConfirmSuf")}`,
                              confirmLabel: t("invites.revoke"),
                              tone: "danger",
                            });
                            if (!ok) return;
                            revokeMutation.mutate({ companyId, inviteId: inv.id });
                          }}
                          disabled={revokeMutation.isPending}
                          className="rounded-md border border-red-500/40 hover:border-red-500 text-red-400 px-2 py-1 text-xs"
                        >
                          {t("invites.revoke")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Shell>
  );
}

function Shell({ title: _title, children }: { title: string; children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-5xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const t = useT();
  const roleLabels: Record<Role, string> = {
    technician: t("invites.roleTechnician"),
    manager: t("invites.roleManager"),
    client: t("invites.roleClient"),
    viewer: t("invites.roleViewer"),
    user: t("invites.roleUser"),
    admin: t("invites.roleAdmin"),
  };
  const colors: Record<Role, string> = {
    technician: "bg-violet-500/20 text-violet-300",
    manager: "bg-teal-500/20 text-teal-300",
    client: "bg-sky-500/20 text-sky-300",
    viewer: "bg-neutral-700/40 text-neutral-300",
    user: "bg-blue-500/20 text-blue-300",
    admin: "bg-amber-500/20 text-amber-300",
  };
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs ${colors[role]}`}>
      {roleLabels[role]}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const t = useT();
  const labels: Record<string, string> = {
    pending: t("invites.statusPending"),
    accepted: t("invites.statusAccepted"),
    revoked: t("invites.statusRevoked"),
    expired: t("invites.statusExpired"),
  };
  const map: Record<string, string> = {
    pending: "bg-blue-500/20 text-blue-300",
    accepted: "bg-lime/20 text-lime",
    revoked: "bg-red-500/20 text-red-300",
    expired: "bg-neutral-700/40 text-neutral-400",
  };
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs ${map[status] ?? ""}`}>
      {labels[status] ?? status}
    </span>
  );
}
